'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import type { StoreApi } from 'zustand/vanilla';
// From the defining modules, not the barrel — see the note in createPlayStore.
import type { Board, BoardMessage, HintBoxState } from '../types';
import {
  defaultNoHintsMessage,
  firstCheckClickMessage,
  noErrorsMessage,
  nothingToCheckMessage,
  type CheckHintContext,
  type GameConfig,
} from './playState';
import { PlayStore } from './createPlayStore';

/**
 * The check/hint feedback layer as plain React state: the board messages + hint box the game UI
 * renders, plus the delayed auto-check reminder. It layers on top of a play store — reading the
 * current cells/board/complete to run the game's check/hint logic, recording Check/Hint usage back
 * into the play store's persisted stats, and reacting to moves (cancel a stale reminder, clear
 * feedback, schedule a new reminder). The dependency is one-way: the play store knows nothing of it.
 */
export function useCheckHintState<BoardT extends Board, CellT>(
  config: GameConfig<BoardT, CellT>,
  playStore: StoreApi<PlayStore<BoardT, CellT>>
): CheckHintContext {
  const [boardMessages, setBoardMessages] = useState<BoardMessage[]>([]);
  const [hintBoxState, setHintBoxState] = useState<HintBoxState>('none');
  // Not rendered — pure logic state for the two-click Check flow. A ref so callbacks read the latest
  // value synchronously without stale closures.
  const hasCheckBeenPressed = useRef(false);
  // Pending delayed auto-check reminder, so a later move can cancel it.
  const autoCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoCheck = useCallback(() => {
    if (autoCheckTimer.current) {
      clearTimeout(autoCheckTimer.current);
      autoCheckTimer.current = null;
    }
  }, []);

  const handleCheckBoard = useCallback(() => {
    const play = playStore.getState();
    if (!play.playData) return;
    clearAutoCheck();
    setHintBoxState('check');
    if (isEqual(play.playData.cells, config.getInitialCells(play.board))) {
      setBoardMessages([nothingToCheckMessage]);
      return;
    }
    play.recordCheckUsed();

    try {
      const errorMsgs = config.checkBoard(play.playData.cells, play.board);
      if (errorMsgs.length < 1) {
        setBoardMessages([noErrorsMessage]);
      } else if (!hasCheckBeenPressed.current && !config.skipCheckConfirmation) {
        // First Check click: warn there are errors; a second click reveals them.
        setBoardMessages([firstCheckClickMessage]);
      } else {
        setBoardMessages(errorMsgs);
      }
      hasCheckBeenPressed.current = true;
    } catch (error) {
      // checkBoard/generateHint assume a well-formed board; a malformed board (bad clue data,
      // mismatched cell/solution shape) throws instead of returning, which otherwise leaves the
      // Check button looking like it did nothing. Log enough to find the bad board.
      console.error('[useCheckHintState] handleCheckBoard failed', {
        gameType: config.gameType,
        boardId: play.board.id,
        cellsShape: [play.playData.cells.length, play.playData.cells[0]?.length],
        error,
      });
    }
  }, [config, playStore, clearAutoCheck]);

  const handleHintRequested = useCallback(() => {
    const play = playStore.getState();
    if (!config.generateHint || !play.playData) return;
    clearAutoCheck();

    try {
      // The hint strategies assume an error-free board, so they only run once the check passes.
      const errors = config.checkBoard(play.playData.cells, play.board);
      if (errors.length > 0) {
        play.recordHintUsed();
        setHintBoxState('check');
        setBoardMessages(errors);
      } else {
        const hint = config.generateHint(play.playData.cells, play.board);
        // A Hint that finds nothing to say isn't held against the player.
        if (hint) play.recordHintUsed();
        setHintBoxState('hint');
        setBoardMessages([hint ?? config.noHintsMessage ?? defaultNoHintsMessage]);
      }
    } catch (error) {
      console.error('[useCheckHintState] handleHintRequested failed', {
        gameType: config.gameType,
        boardId: play.board.id,
        cellsShape: [play.playData.cells.length, play.playData.cells[0]?.length],
        error,
      });
    }
  }, [config, playStore, clearAutoCheck]);

  const isHintAvailable = useCallback(() => {
    const play = playStore.getState();
    if (!config.generateHint || !play.playData) return false;
    try {
      // Pressing Hint on a board with errors shows the errors, which is worth pressing for too.
      if (config.checkBoard(play.playData.cells, play.board).length > 0) return true;
      return config.generateHint(play.playData.cells, play.board) !== null;
    } catch {
      // handleHintRequested logs the same failure if the player presses Hint.
      return false;
    }
  }, [config, playStore]);

  // React to play-data changes: cancel any pending reminder, clear feedback on a move (for games that
  // clear messages on move), and schedule a fresh auto-check reminder when the board is filled but wrong.
  useEffect(() => {
    const unsubscribe = playStore.subscribe((state, prev) => {
      // Fire only when the cells reference changes — a Check/Hint bumps playStats (same cells reference).
      if (state.playData.cells === prev.playData.cells) return;
      clearAutoCheck();
      if (config.clearBoardMessages) {
        setHintBoxState('none');
        setBoardMessages([]);
        hasCheckBeenPressed.current = false;
      }
      if (!state.complete && config.shouldAutoCheck(state)) {
        autoCheckTimer.current = setTimeout(() => {
          autoCheckTimer.current = null;
          const laterState = playStore.getState(); // Re-read the store here: this fires 1s later and should validate the latest board.
          const laterErrors = config.checkBoard(laterState.playData.cells, laterState.board);
          if (laterErrors.length < 1) return;
          setHintBoxState('check');
          setBoardMessages([firstCheckClickMessage]);
          hasCheckBeenPressed.current = true;
        }, 1000);
      }
    });
    return () => {
      unsubscribe();
      clearAutoCheck();
    };
  }, [config, playStore, clearAutoCheck]);

  return useMemo(
    () => ({
      boardMessages,
      hintBoxState,
      handleCheckBoard,
      handleHintRequested,
      isHintAvailable,
      setHintBoxState,
      setBoardMessages,
    }),
    [boardMessages, hintBoxState, handleCheckBoard, handleHintRequested, isHintAvailable]
  );
}
